import { AlertCircle } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import { GlassPanel } from '@/components/glass/GlassPanel';
import type { AgentReportEntry, AgentReportKey } from '@/types/db';
import { cn } from '@/lib/utils';

const TAB_ORDER: { key: AgentReportKey; label: string }[] = [
  { key: 'compiled', label: 'Compiled Report' },
  { key: 'walkability', label: 'Walkability' },
  { key: 'entertainment', label: 'Entertainment' },
  { key: 'dog_friendliness', label: 'Dog Friendliness' },
  { key: 'flood', label: 'Flood' },
  { key: 'internet', label: 'Internet' },
  { key: 'property_photos', label: 'Photos' },
];

export interface AgentReportTabsProps {
  agentReports: Record<AgentReportKey, AgentReportEntry>;
  className?: string;
}

/**
 * `<AgentReportTabs>` — Radix Tabs over the 7 agent reports. Per UI-SPEC
 * § 3.5.
 *
 * Default tab: `compiled` (the master `house_hunter_report.md`) — most
 * useful first read on the page (CONTEXT.md "Claude's Discretion").
 *
 * TabsTrigger active state: `tide-400/15` bg + `tide-400/40` border + foam
 * text. Failed tabs show a small coral `<AlertCircle>` next to the label.
 *
 * Markdown rendering: server-side via `marked` (Phase 06 backend exposes
 * `agent_reports[key].html` in the round-trip). Rendered into a
 * `prose-glass`-styled wrapper using `dangerouslySetInnerHTML` — author-
 * trusted (.md generated by our own agents); marked default escapes raw
 * HTML in source for defense-in-depth.
 *
 * Long content: plain `overflow-y-auto max-h-[80vh]` per CONTEXT.md Q2
 * default — no `<ScrollArea>` install required at this content size.
 *
 * Tab switching: instant (Radix default). Keyboard: ArrowLeft/Right cycle,
 * Home/End jump (Radix-provided).
 *
 * ARIA: Radix sets `role="tablist"`, `role="tab"`, `aria-selected`,
 * `role="tabpanel"`. No additional ARIA needed.
 */
export function AgentReportTabs({
  agentReports,
  className,
}: AgentReportTabsProps) {
  return (
    <Tabs defaultValue="compiled" className={cn('w-full', className)}>
      <TabsList
        className={cn(
          'flex gap-1 overflow-x-auto bg-ocean-900/60 backdrop-blur-md',
          'border-b border-foam-50/10 rounded-t-2xl px-2 py-2 h-auto',
          'justify-start',
        )}
      >
        {TAB_ORDER.map(({ key, label }) => {
          const entry = agentReports[key];
          const isFailed = entry?.status === 'failed';
          return (
            <TabsTrigger
              key={key}
              value={key}
              className={cn(
                'px-4 h-10 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0',
                'text-foam-50/70 hover:text-foam-50',
                'data-[state=active]:bg-tide-400/15 data-[state=active]:text-foam-50',
                'data-[state=active]:border data-[state=active]:border-tide-400/40',
                'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
              )}
            >
              {label}
              {isFailed && (
                <AlertCircle
                  aria-label="Report failed"
                  className="inline-block ml-1 w-3 h-3 text-coral-500"
                />
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
      {TAB_ORDER.map(({ key }) => (
        <TabsContent key={key} value={key} className="mt-0">
          <GlassPanel
            variant="strong"
            className={cn(
              'p-6 rounded-t-none rounded-b-2xl',
              'min-h-[300px] md:min-h-[400px]',
            )}
          >
            <ReportPanel entry={agentReports[key]} />
          </GlassPanel>
        </TabsContent>
      ))}
    </Tabs>
  );
}

function ReportPanel({ entry }: { entry: AgentReportEntry | undefined }) {
  if (!entry) {
    return <p className="text-foam-50/60">Report unavailable.</p>;
  }
  if (entry.status === 'pending') {
    return <p className="text-foam-50/60">Report still being generated…</p>;
  }
  if (entry.status === 'failed') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle aria-hidden className="w-5 h-5 text-coral-500" />
          <h2 className="text-xl font-semibold text-foam-50">
            This report failed to generate.
          </h2>
        </div>
        <p className="text-foam-50/70">
          {entry.errorMessage ||
            'The agent encountered an error. Try re-running this property.'}
        </p>
      </div>
    );
  }
  if (!entry.html) {
    return <p className="text-foam-50/60">Report has no content.</p>;
  }
  // Author-trusted (.md generated by our own agents). `marked` default
  // escapes raw HTML in source — defense-in-depth per CONTEXT.md.
  return (
    <div
      className="prose-glass max-h-[80vh] overflow-y-auto"
      dangerouslySetInnerHTML={{ __html: entry.html }}
    />
  );
}
